import React, { useEffect, useMemo, useState } from "react";
import QuizGenerationForm from "./QuizGenerationForm";
import { useCallback } from "react";
import {
  questionDifficultyLevels,
  questionFormats,
  questionType,
} from "constants/Constants";
import { DisplayGeneratedQnA } from "components/courses/add-new-course/steps/quiz-genaration/DisplayGeneratedQnA";
import { Card, Form, Row, Col } from "react-bootstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import dayjs from "dayjs";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { Info } from "react-feather";
import { useCookies } from "react-cookie";
import { 
  generateQNAAPI, 
  getvectorisedResourcesAPI 
} from "ServerAPI/serverAPI";
import { useToast } from "context/toast-context/ToastProvider";

const QuestionGenerationContainer = ({
  onSubmitQuiz,
  quizGenerationResponseData,
  handleGeneratedQuizSubmit,
  setQuizGenerationResponseData,
  isGenerating,
  quizTimer,
  setQuizTimer,
  activeSection = null,
  setIsGenerating,
  initialQuizData,
  scheduledAttempt,
  setScheduledAttempt,
  startDate,
  setStartDate,
  startTime,
  setStartTime,
  endDate,
  setEndDate,
  endTime,
  setEndTime,
  passingPercentage,
  setPassingPercentage,
  finalAssessment,
  setFinalAssessment,
  isProctored,
  setIsProctored,
  maxAttempts,
  setMaxAttempts,
  allowMultipleAttempts,
  setAllowMultipleAttempts,
  courseDomain,
  courseDepartment,
  learningOutcomes = [],
}) => {
  const { showToast } = useToast();
  const [isLoadingResources, setIsLoadingResources] = useState(false);

  // === MODULAR STATE MATCHING qna-generation.js EXACTLY ===
  const [questionConfigs, setQuestionConfigs] = useState({
    mcq: { enabled: true, count: 1, marks: 1, difficulty: 'easy', taxonomy: 'Remember', outcomes: {} },
    fill_in_the_blanks: { enabled: false, count: 1, marks: 1, difficulty: 'easy', taxonomy: 'Understand', outcomes: {} },
    true_or_false: { enabled: false, count: 1, marks: 1, difficulty: 'easy', taxonomy: 'Understand', outcomes: {} },
    very_short_answer: { enabled: false, count: 1, marks: 2, difficulty: 'easy', taxonomy: 'Remember', outcomes: {} },
    short_answer: { enabled: false, count: 1, marks: 3, difficulty: 'easy', taxonomy: 'Understand', outcomes: {} },
    medium_answer: { enabled: false, count: 1, marks: 5, difficulty: 'easy', taxonomy: 'Apply', outcomes: {} },
    long_answer: { enabled: false, count: 1, marks: 10, difficulty: 'easy', taxonomy: 'Analyze', outcomes: {} },
    essay_answer: { enabled: false, count: 1, marks: 15, difficulty: 'easy', taxonomy: 'Evaluate', outcomes: {} },
  });

  const [orgConfig, setOrgConfig] = useState({
    selectedDomain: courseDomain || "",
    selectedDepartment: courseDepartment || "",
    vectorisedFile: "",
    passingPercentage: passingPercentage || 70,
    finalAssessment: finalAssessment || false
  });

  const [cookies] = useCookies(["userId", "email", "organizationId"]);
  const [vectorisedResources, setVectorisedResources] = useState([]);

  // === FETCH VECTORISED RESOURCES ===
  const fetchVectorisedResources = useCallback(async () => {
    if (!cookies.userId) return;

    try {
      setIsLoadingResources(true);
      const response = await getvectorisedResourcesAPI(cookies.userId, activeSection?.sectionId);
      const rawData = response.data;
      const resources = Array.isArray(rawData) ? rawData : [rawData];
      setVectorisedResources(resources);
    } catch (err) {
      console.error("Failed to load vectorised resources:", err);
      setVectorisedResources([]);
    } finally {
      setIsLoadingResources(false);
    }
  }, [cookies.userId, activeSection?.sectionId]);

  useEffect(() => {
    fetchVectorisedResources();
  }, [fetchVectorisedResources]);

  // Update orgConfig if course metadata changes
  useEffect(() => {
    setOrgConfig(prev => ({
      ...prev,
      selectedDomain: courseDomain || "",
      selectedDepartment: courseDepartment || ""
    }));
  }, [courseDomain, courseDepartment]);

  const totalQuestion = useMemo(() => {
    return Object.values(questionConfigs)
      .filter(config => config.enabled)
      .reduce((sum, config) => sum + Number(config.count || 0), 0);
  }, [questionConfigs]);

  const submitQuizGenerationForm = async () => {
    if (totalQuestion === 0) {
      showToast("Please enable at least one question type and set a count.", "warning");
      return;
    }

    if (!orgConfig.vectorisedFile) {
      showToast("Please select a Resource file for generation.", "warning");
      return;
    }

    const mandatoryQuestions = Object.entries(questionConfigs)
      .filter(([_, config]) => config.enabled && config.count > 0)
      .map(([type, config]) => {
        const item = {
          question_type: type,
          total_questions: Number(config.count),
          bloom_taxonomy: config.taxonomy,
          marks_per_question: Number(config.marks)
        };

        // Add outcome distribution if outcomes are mapped
        const validOutcomes = Object.entries(config.outcomes || {})
          .filter(([_, count]) => Number(count) > 0)
          .reduce((acc, [co, count]) => ({ ...acc, [co]: Number(count) }), {});

        if (Object.keys(validOutcomes).length > 0) {
          item.course_outcome_distribution = validOutcomes;
        }

        return item;
      });

    // Debug: Log cookie values to find why org_id is empty
    console.log("DEBUG cookies object:", cookies);
    console.log("DEBUG cookies.organizationId:", cookies.organizationId);
    
    // Fallback: read organizationId directly from document.cookie if react-cookie fails
    const getOrgIdFromCookie = () => {
      const match = document.cookie.match(/(?:^|;\s*)organizationId=([^;]*)/);
      return match ? decodeURIComponent(match[1]) : "";
    };
    
    const orgId = cookies.organizationId || getOrgIdFromCookie() || "781bb6da-7908-41e4-86ab-8676bc085c56";

    const payload = {
      pdf_filename: orgConfig.vectorisedFile,
      org_id: orgId,
      domain: orgConfig.selectedDomain,
      department: orgConfig.selectedDepartment,
      mandatory: mandatoryQuestions
    };

    try {
      setIsGenerating(true);
      const response = await generateQNAAPI(payload, activeSection?.sectionId);
      
      const result = response.data;
      if (result.status === false) {
        showToast(result.message || "Failed to generate QnA.", "error");
        setQuizGenerationResponseData(null);
      } else {
        setQuizGenerationResponseData(result);
        showToast("QnA generated successfully!", "success");
      }
    } catch (error) {
      console.error("API Error:", error);
      showToast("Generation failed due to a network error.", "error");
    } finally {
      setIsGenerating(false);
    }
  };

  const transformedData = useMemo(() => {
    if (!quizGenerationResponseData || !quizGenerationResponseData.status) return [];
    
    const flatData = [];
    const actualData = quizGenerationResponseData.data || {};

    // Mapping backend keys to DisplayGeneratedQnA expected keys
    const typeMapping = {
      mcq: 'mcq',
      fill_in_the_blanks: 'fill_in_the_blanks',
      true_or_false: 'true_false',
      very_short_answer: 'very_short_answer',
      short_answer: 'short_answer',
      medium_answer: 'medium_answer',
      long_answer: 'long_answer',
      essay_answer: 'essay_answer'
    };

    Object.entries(typeMapping).forEach(([backendKey, frontendKey]) => {
      const questions = actualData[backendKey] || [];
      const config = questionConfigs[backendKey];
      
      questions.forEach((q, idx) => {
        const transformedQ = {
          id: `${frontendKey}-${idx}-${Date.now()}`,
          type: frontendKey,
          // difficulty: q.difficulty || config?.difficulty || "easy", // CRITICAL FIX: Missing difficulty was breaking groupedData keys
          question: q.question,
          explanation: q.explanation || "",
          marks: q.marks || config?.marks || 1,
          courseOutcome: q.course_outcome || "",
          taxonomy: q.taxonomy_level || q.taxonomy || config?.taxonomy || "Remember",
          topics: q.topics_covered || [],
          correctAnswer: q.answer,
          chunkId: q.chunk_id || "",
          rubric: Array.isArray(q.rubric) ? q.rubric : [],
          answers: [] // Default empty to avoid map error
        };

        const normalizedKey = frontendKey.toLowerCase();

        if (normalizedKey === 'mcq' && q.options) {
          transformedQ.answers = q.options.map((opt, i) => ({
            answer: opt.text,
            value: String.fromCharCode(97 + i).toLowerCase() // a, b, c, d
          }));
          const correctOpt = q.options.find(o => o.isCorrect) || q.options.find(o => o.text === q.answer);
          if (correctOpt) {
            const optIndex = q.options.indexOf(correctOpt);
            transformedQ.correctAnswer = String.fromCharCode(97 + optIndex).toLowerCase();
          }
          transformedQ.answerSelectionType = "single";
          transformedQ.interface = "radio";
        } else if (normalizedKey === 'true_false') {
          transformedQ.answers = [
            { answer: "True", value: "True" },
            { answer: "False", value: "False" }
          ];
          transformedQ.correctAnswer = q.answer;
          transformedQ.answerSelectionType = "single";
          transformedQ.interface = "radio";
        } else if (normalizedKey.includes('fill')) {
          transformedQ.answerSelectionType = "fill_in_the_blanks";
          transformedQ.interface = "text";
        } else if (
          normalizedKey.includes('answer') || 
          normalizedKey.includes('subjective') || 
          normalizedKey.includes('essay')
        ) {
          transformedQ.answerSelectionType = "subjective";
          transformedQ.interface = "textarea";
        } else {
          transformedQ.answerSelectionType = "single";
          transformedQ.interface = "radio";
        }

        flatData.push(transformedQ);
      });
    });

    return flatData;
  }, [quizGenerationResponseData, questionConfigs]);

  if (quizGenerationResponseData && quizGenerationResponseData.status !== false && Object.keys(quizGenerationResponseData).length > 0) {
    return (
      <>
        {/* Quiz Metadata - always visible when reviewing generated QnA */}
        <Card className="mb-4 border-light shadow-none">
          <Card.Body className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom py-2">
              <h4 className="mb-0">Quiz Metadata</h4>
            </div>
            <Row className="g-3">
              <Col xs={12} md={6} lg={4}>
                <div className="mb-3">
                  <label className="form-label fw-medium">Passing Percentage</label>
                  <Form.Control
                    type="number"
                    id="passing-percentage-review"
                    placeholder="Enter passing percentage"
                    name="passingPercentage"
                    value={passingPercentage}
                    onChange={(e) => setPassingPercentage(e.target.value > 100 ? 100 : e.target.value)}
                    onWheel={(e) => e.target.blur()}
                    className="text-dark"
                    min="0"
                    max="100"
                  />
                </div>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <div className="mb-3">
                  <label className="form-label fw-medium d-flex align-items-center">
                    Final Assessment
                    {allowMultipleAttempts && (
                      <Tippy content="Not applicable when Allow Multiple Attempts is enabled — a final assessment is a one-shot evaluation.">
                        <span className="ms-2">
                          <Info size={16} className="text-muted" />
                        </span>
                      </Tippy>
                    )}
                  </label>
                  <div className="mt-2">
                    <Form.Check
                      name="finalAssessment"
                      onChange={(e) => setFinalAssessment(e.target.checked)}
                      checked={!allowMultipleAttempts && finalAssessment}
                      disabled={allowMultipleAttempts}
                      label="Mark as final assessment"
                      id="final-assessment-check-review"
                    />
                  </div>
                </div>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <div className="mb-3">
                  <label className="form-label fw-medium">Quiz Timer (Minutes)</label>
                  <Form.Control
                    type="number"
                    id="quiz-timer-review"
                    placeholder="Enter quiz duration"
                    name="quizTimer"
                    value={quizTimer}
                    onChange={(e) => setQuizTimer(Number(e.target.value))}
                    onWheel={(e) => e.target.blur()}
                    className="text-dark"
                    min="1"
                  />
                </div>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <div className="mb-3">
                  <label className="form-label fw-medium d-flex align-items-center">
                    Allow Multiple Attempts
                    <Tippy content={finalAssessment ? "Not applicable when Final Assessment is enabled." : "Not applicable when Scheduled or Proctored Quiz is enabled."}>
                      <span className="ms-2">
                        <Info size={16} className="text-muted" />
                      </span>
                    </Tippy>
                  </label>
                  <div className="mt-2">
                    <Form.Check
                      type="switch"
                      id="allow-multiple-attempts-switch-review"
                      checked={(scheduledAttempt || isProctored || finalAssessment) ? false : allowMultipleAttempts}
                      onChange={(e) => setAllowMultipleAttempts(e.target.checked)}
                      disabled={scheduledAttempt || isProctored || finalAssessment}
                      label={(scheduledAttempt || isProctored || finalAssessment) || !allowMultipleAttempts ? "No Attempt Tracking" : "Multiple Attempts"}
                    />
                  </div>
                </div>
              </Col>
              {allowMultipleAttempts && !(scheduledAttempt || isProctored || finalAssessment) && (
                <Col xs={12} md={6} lg={4}>
                  <div className="mb-3">
                    <label className="form-label fw-medium">Max Attempts</label>
                    <Form.Control
                      type="number"
                      id="max-attempts-review"
                      placeholder="Enter max attempts allowed"
                      name="maxAttempts"
                      value={maxAttempts}
                      onChange={(e) => setMaxAttempts(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      className="text-dark"
                      min="2"
                    />
                  </div>
                </Col>
              )}
              <Col xs={12} md={6} lg={4}>
                <div className="mb-3">
                  <label className="form-label fw-medium d-flex align-items-center">
                    Scheduled Quiz
                    <Tippy content="If enabled, students can only take the quiz within the specified window.">
                      <span className="ms-2">
                        <Info size={16} className="text-muted" />
                      </span>
                    </Tippy>
                  </label>
                  <div className="mt-2">
                    <Form.Check
                      type="switch"
                      id="scheduled-quiz-switch-review"
                      checked={scheduledAttempt}
                      onChange={(e) => setScheduledAttempt(e.target.checked)}
                      label={scheduledAttempt ? "Scheduled" : "Always Available"}
                    />
                  </div>
                </div>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <div className="mb-3">
                  <label className="form-label fw-medium d-flex align-items-center">
                    Proctored Quiz
                    <Tippy content="Enabling proctoring will apply Fullscreen Enforcement, Tab/Window Switch Detection, Copy-Paste & Right-Click Disable, and DevTools/Shortcut Blocking when attempting this quiz.">
                      <span className="ms-2">
                        <Info size={16} className="text-muted" />
                      </span>
                    </Tippy>
                  </label>
                  <div className="mt-2">
                    <Form.Check
                      type="switch"
                      id="proctoring-switch-review"
                      checked={isProctored}
                      onChange={(e) => setIsProctored(e.target.checked)}
                      label={isProctored ? "Proctoring Enabled" : "Proctoring Disabled"}
                    />
                  </div>
                </div>
              </Col>
              {scheduledAttempt && (
                <>
                  <Col xs={12} md={6}>
                    <div className="mb-3">
                      <label className="form-label fw-medium text-primary">Access Opens (Start)</label>
                      <Row className="g-2">
                        <Col>
                          <Flatpickr
                            value={startDate}
                            onChange={([date]) => setStartDate(date ? dayjs(date).format("YYYY-MM-DD") : "")}
                            className="form-control text-dark"
                            placeholder="Select Start Date"
                            options={{ dateFormat: "Y-m-d" }}
                          />
                        </Col>
                        <Col>
                          <MobileTimePicker
                            value={startTime ? dayjs(startTime, "HH:mm") : null}
                            onChange={(v) => setStartTime(v ? v.format("HH:mm") : "")}
                            views={["hours", "minutes"]}
                            openTo="hours"
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                                className: "bg-white",
                                sx: { "& .MuiInputBase-root": { height: "38px" } },
                              },
                            }}
                          />
                        </Col>
                      </Row>
                    </div>
                  </Col>
                  <Col xs={12} md={6}>
                    <div className="mb-3">
                      <label className="form-label fw-medium text-danger">Access Closes (End)</label>
                      <Row className="g-2">
                        <Col>
                          <Flatpickr
                            value={endDate}
                            onChange={([date]) => setEndDate(date ? dayjs(date).format("YYYY-MM-DD") : "")}
                            className="form-control text-dark"
                            placeholder="Select End Date"
                            options={{ dateFormat: "Y-m-d" }}
                          />
                        </Col>
                        <Col>
                          <MobileTimePicker
                            value={endTime ? dayjs(endTime, "HH:mm") : null}
                            onChange={(v) => setEndTime(v ? v.format("HH:mm") : "")}
                            views={["hours", "minutes"]}
                            openTo="hours"
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                                className: "bg-white",
                                sx: { "& .MuiInputBase-root": { height: "38px" } },
                              },
                            }}
                          />
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </>
              )}
            </Row>
          </Card.Body>
        </Card>

        <DisplayGeneratedQnA
          responseData={transformedData}
          handleGeneratedQuizSubmit={handleGeneratedQuizSubmit}
          passingPerctage={passingPercentage}
          finalAssessment={finalAssessment}
          quizTimer={quizTimer}
          activeSection={activeSection}
          effectiveCourseId={cookies.userId}
          learningOutcomes={learningOutcomes}
        />
      </>
    );
  }

  return (
    <QuizGenerationForm
      questionConfigs={questionConfigs}
      setQuestionConfigs={setQuestionConfigs}
      orgConfig={orgConfig}
      setOrgConfig={setOrgConfig}
      handleOnSubmit={submitQuizGenerationForm}
      totalQuestions={totalQuestion}
      isGenerating={isGenerating}
      quizTimer={quizTimer}
      setQuizTimer={setQuizTimer}
      vectorisedResources={vectorisedResources}
      isLoadingResources={isLoadingResources}
      scheduledAttempt={scheduledAttempt}
      setScheduledAttempt={setScheduledAttempt}
      startDate={startDate}
      setStartDate={setStartDate}
      startTime={startTime}
      setStartTime={setStartTime}
      endDate={endDate}
      setEndDate={setEndDate}
      endTime={endTime}
      setEndTime={setEndTime}
      passingPercentage={passingPercentage}
      setPassingPercentage={setPassingPercentage}
      finalAssessment={finalAssessment}
      setFinalAssessment={setFinalAssessment}
      isProctored={isProctored}
      setIsProctored={setIsProctored}
      maxAttempts={maxAttempts}
      setMaxAttempts={setMaxAttempts}
      allowMultipleAttempts={allowMultipleAttempts}
      setAllowMultipleAttempts={setAllowMultipleAttempts}
      learningOutcomes={learningOutcomes}
    />
  );
};

export default QuestionGenerationContainer;